import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data-dropdown-open", "disabled"];
let _ = t => t,
  _t,
  _t2;
import { useFetchContentAssistanceContent } from 'content-assistance-lib/hooks/useFetchContentAssistanceContent';
import enviro from 'enviro';
import { getFullUrl } from 'hubspot-url-utils';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLangLocale from 'I18n/utils/getLangLocale';
import PortalIdParser from 'PortalIdParser';
import Raven from 'raven-js';
import { createContext, useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TextToolbarIcon } from 'rich-text-lib/components/editor/TextToolbarIcon';
import { BreezeSingleStarIcon } from 'foundations-assets';
import { ContentAssistantOnboardingPopover } from './ContentAssistantOnboardingPopover';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import { findNode } from '../../utils/nodeUtils';
import { schema } from '../../ProsemirrorSchema';
import { commands } from './ContentAssistantTypes';
import styledWithTheme from 'foundations-theming/utils';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { MultiLevelDropdown } from 'foundations-components/transitional/components/Dropdown';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledLoadingSpinner = styledWithTheme(LoadingSpinner)(_t || (_t = _`
  height: 25px;
  width: 25px;
`));
const StyledIcon = styledWithTheme(TextToolbarIcon)(_t2 || (_t2 = _`
  color: ${0}};
`), ({
  disabled,
  theme
}) => disabled ? theme.color['text-core-disabled'] : theme.color['fill-accent-light-orange-default']);
const StyledAIContainer = styled.section.withConfig({
  displayName: "ContentAssistant__StyledAIContainer",
  componentId: "sc-18imzyf-0"
})(["position:absolute;"]);
const FetchingContentContext = /*#__PURE__*/createContext(false);
const ContentAssistantButton = _ref => {
  let {
      'data-dropdown-open': isDropdownOpen,
      disabled
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const isFetching = useContext(FetchingContentContext);
  const {
    CONTENT_ASSISTANT: {
      globalSettingEnabled = false,
      shouldShowAIOnboardingPopover = false,
      setHasSeenAIOnboardingPopover = () => {}
    } = {}
  } = useContext(EditorConfigContext);
  const [AIInfoIconOpen, setAIInfoIconOpen] = useState(false);
  const settingsUrl = `${getFullUrl('app')}/ai-settings/${PortalIdParser.get()}`;
  return /*#__PURE__*/_jsx(RTEToolbarButton, Object.assign({}, rest, {
    "data-test-id": "content-assistant",
    disabled: disabled,
    selected: isDropdownOpen,
    style: {
      position: 'relative',
      minWidth: '24px',
      cursor: 'pointer'
    },
    children: isFetching ? /*#__PURE__*/_jsx(StyledLoadingSpinner, {
      size: "extra-small",
      use: 'primary-white',
      margin: "0"
    }) : /*#__PURE__*/_jsxs(StyledAIContainer, {
      children: [shouldShowAIOnboardingPopover && /*#__PURE__*/_jsx(ContentAssistantOnboardingPopover, {
        AIInfoIconOpen,
        setAIInfoIconOpen,
        setHasSeenAIOnboardingPopover,
        settingsUrl
      }), /*#__PURE__*/_jsx(Tooltip, {
        open: isDropdownOpen ? false : undefined,
        title: globalSettingEnabled ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: disabled ? 'hubspot-prosemirror.tooltips.contentAssistantDisabled' : 'hubspot-prosemirror.tooltips.contentAssistant'
        }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "hubspot-prosemirror.tooltips.contentAssistantSettingOff_jsx",
          elements: {
            Link
          },
          options: {
            href: settingsUrl,
            external: true,
            onClick: e => {
              e.stopPropagation();
            }
          }
        }),
        children: /*#__PURE__*/_jsx(StyledIcon, {
          disabled: disabled,
          icon: BreezeSingleStarIcon
        })
      })]
    })
  }));
};
const tones = ['friendly', 'heartfelt', 'professional'];
const ContentAssistant = () => {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const {
    CONTENT_ASSISTANT: {
      commandType = 'inbox',
      globalSettingEnabled = false,
      trackOpenPopover = undefined,
      trackSelect = undefined
    } = {}
  } = useContext(EditorConfigContext);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const {
    fetch,
    request
  } = useFetchContentAssistanceContent({
    language: getLangLocale(),
    onError: error => {
      console.warn('Failed to generate AI content');
      Raven.captureException(error);
    },
    onSuccess: response => {
      const tr = editorState.tr.insertText(response[0].text);
      const nextState = editorState.apply(tr);
      setEditorState(nextState);
    }
  });
  const onSelect = useCallback((commandId, toneOfVoice) => {
    const emailReplyHistory = findNode(editorState.doc, node => {
      return node.type === schema.nodes.emailReplyHistory;
    });
    let aiEditorState = editorState;
    if (emailReplyHistory) {
      aiEditorState = aiEditorState.apply(aiEditorState.tr.delete(emailReplyHistory.pos, emailReplyHistory.pos + emailReplyHistory.node.nodeSize));
    }
    const editorContext = mapEditorContext(aiEditorState);
    if (trackSelect) {
      trackSelect({
        choice: toneOfVoice,
        command: commandId,
        selected: commandId
      });
    }
    fetch({
      commandId,
      parameters: {
        editorContext,
        toneOfVoice
      },
      resultCount: 1
    });

    // @ts-expect-error hsFeedback does indeed exist
    if (window.hsFeedback && enviro.isProd()) {
      // @ts-expect-error hsFeedback does indeed exist
      window.hsFeedback.loadSurvey('CSAT', 933);
    }
    function mapEditorContext(state) {
      const plain = convertToPlaintextState(state);
      const precedingText = plain.doc.textBetween(0, plain.selection.from, '\n');
      const selectedText = plain.doc.textBetween(plain.selection.from, plain.selection.to, '\n');
      const rest = plain.doc.textBetween(plain.selection.from, plain.doc.content.size, '\n').replace(selectedText, `<<${selectedText}>>`);
      return `${precedingText}${rest}`;
    }
  }, [editorState, fetch, trackSelect]);
  const options = [{
    label: I18n.text('hubspot-prosemirror.contentAssistant.commands.rewrite'),
    // @ts-expect-error Not in types but recommended in docs
    onClick: () => onSelect(commands[commandType].rewrite)
  }, {
    label: I18n.text('hubspot-prosemirror.contentAssistant.commands.expand'),
    // @ts-expect-error Not in types but recommended in docs
    onClick: () => onSelect(commands[commandType].expand)
  }, {
    label: I18n.text('hubspot-prosemirror.contentAssistant.commands.shorten'),
    // @ts-expect-error Not in types but recommended in docs
    onClick: () => onSelect(commands[commandType].shorten)
  }, {
    label: I18n.text('hubspot-prosemirror.contentAssistant.commands.changeTone'),
    options: tones.map(tone => ({
      label: I18n.text(`hubspot-prosemirror.contentAssistant.commands.tones.${tone}`),
      onClick: () => onSelect(commands[commandType].changeTone, tone)
    }))
  }, {
    label: I18n.text('hubspot-prosemirror.contentAssistant.commands.draft'),
    // @ts-expect-error Not in types but recommended in docs
    onClick: () => onSelect(commands[commandType].draft)
  }];
  return /*#__PURE__*/_jsx(FetchingContentContext.Provider, {
    value: request === 'STARTED',
    children: /*#__PURE__*/_jsx(MultiLevelDropdown, {
      Button: ContentAssistantButton,
      buttonUse: 'transparent',
      disabled: !globalSettingEnabled || editorState.selection.empty,
      open: isDropdownOpen,
      onOpenChange: e => {
        setDropdownOpen(e.target.value);
      },
      onOpenComplete: () => {
        if (trackOpenPopover) {
          trackOpenPopover();
        }
      },
      options: options
    })
  });
};
export default ContentAssistant;